/** @jsx jsx */
import React, { ReactElement, useState } from 'react'
import { jsx } from '@emotion/react'
import styled from '@emotion/styled'
import { UIDReset, UIDConsumer } from 'react-uid'

import theme from '../../theme'
import { ThemeType } from '../../typings/emotion'

export type varType =
  | 'primary'
  | 'link'
  | 'gray'
  | 'grayLighter'
  | 'grayDark'
  | 'white'
  | 'black'
  | 'green'
  | 'red'
  | 'pink'
  | 'green'
  | 'blue'
  | 'gold'
  | 'iconBlue'
  | 'iconGreen'
  | 'iconRed'
  | 'lightBlue'
  | 'magenta'
  | 'olive'
  | 'orange'
  | 'success'
  | 'vimr'

function variants(t: ThemeType, variant: varType) {
  const variantTypes = {
    primary: {
      default: t.colors.primary,
      isDisabled: t.colors.grays.grayLight,
    },
    success: {
      default: t.colors.success,
      isDisabled: t.colors.grays.grayLight,
    },
    link: {
      default: t.colors.textLink,
      isDisabled: t.colors.grays.grayLight,
    },
    gray: {
      default: t.colors.iconGray,
      isDisabled: t.colors.grays.grayLight,
    },
    grayLighter: {
      default: t.colors.grays.grayLighter,
      isDisabled: t.colors.grays.grayLighter,
    },
    grayDark: {
      default: t.colors.grays.grayDarker,
      isDisabled: t.colors.grays.grayLight,
    },
    white: {
      default: t.colors.whites.white,
      isDisabled: t.colors.grays.grayLight,
    },
    black: {
      default: 'black',
      isDisabled: t.colors.grays.grayLight,
    },
    green: {
      default: t.colors.main.success,
      isDisabled: t.colors.grays.grayLight,
    },
    red: {
      default: t.colors.icons.iconWarning,
      isDisabled: t.colors.grays.grayLight,
    },
    pink: {
      default: t.colors.icons.iconPink,
      isDisabled: t.colors.grays.grayLight,
    },
    gold: {
      default: t.colors.gold,
      isDisabled: t.colors.grays.grayLight,
    },
    iconBlue: {
      default: t.colors.icons.iconBlue,
      isDisabled: t.colors.grays.grayLight,
    },
    iconGreen: {
      default: t.colors.icons.iconGreen,
      isDisabled: t.colors.grays.grayLight,
    },
    iconRed: {
      default: t.colors.icons.iconRed,
      isDisabled: t.colors.grays.grayLight,
    },
    lightBlue: {
      default: t.colors.icons.lightBlue,
      isDisabled: t.colors.grays.grayLight,
    },
    magenta: {
      default: t.colors.icons.magenta,
      isDisabled: t.colors.grays.grayLight,
    },
    olive: {
      default: t.colors.icons.olive,
      isDisabled: t.colors.grays.grayLight,
    },
    orange: {
      default: t.colors.icons.orange,
      isDisabled: t.colors.grays.grayLight,
    },
    vimr: {
      default: t.colors.vimr,
      isDisabled: t.colors.grays.grayLight,
    },
  }
  return variantTypes[variant]
}

interface SVGProps {
  disablePointerEvents?: boolean
  rotate?: string
  isDisabled?: boolean
  isHoverable?: boolean
  iconRight?: boolean
  inverted?: boolean
  fillStyle: any
}

const SVG = styled.svg<SVGProps>(
  ({ disablePointerEvents }) =>
    !!disablePointerEvents && { pointerEvents: 'none' },
  ({ rotate }) => !!rotate && { transform: `rotate(${rotate})` },
  ({ theme: t, isDisabled, isHoverable, fillStyle }) => ({
    cursor: !!isDisabled ? 'not-allowed' : 'pointer',
    transition: 'opacity: 0.3s',
    ':hover': {
      opacity: !!isHoverable && !isDisabled ? t.opacities.hoverIcon : 1,
    },
    fill: isDisabled ? fillStyle.isDisabled : fillStyle.default,
  }),
  // TODO: set hover to white if inverted (this doesnt work)
  ({ theme: t, inverted }) =>
    !!inverted && {
      '&:hover': {
        fill: t.colors.white,
      },
    },
  ({ iconRight }) => !!iconRight && { marginLeft: '0.5em' }
)

export interface Props {
  children?: ReactElement | ReactElement[]
  disablePointerEvents?: boolean
  id?: string
  /** If true, this Icon will render itself with disabled styles */
  isDisabled?: boolean
  /** If true, this Icon has a hover effect */
  isHoverable?: boolean
  inverted?: boolean
  rotate?: string
  size?: number
  width?: string
  maxWidth?: string
  title?: string
  variant?: varType
  viewBox?: string
  iconRight?: boolean
  styles?: any
  onClick?: (e: React.MouseEvent) => void
}

const Icon: React.FC<Props> = ({
  children,
  disablePointerEvents,
  id,
  isDisabled,
  rotate,
  size = 14, // standard size to nicely fit into an element with heightInput
  title = '',
  viewBox,
  isHoverable = true,
  variant = 'primary',
  inverted,
  iconRight,
  styles = {},
  width,
  maxWidth,
  onClick,
}) => {
  const fill = variants(theme, variant)
  return (
    <UIDReset>
      <UIDConsumer>
        {(uid: string) => (
          <SVG
            id={id}
            xmlns="http://www.w3.org/2000/svg"
            role="img"
            aria-labelledby="title"
            viewBox={viewBox}
            width={width ? width : size}
            height={width ? undefined : size}
            fillStyle={fill}
            disablePointerEvents={disablePointerEvents}
            rotate={rotate}
            inverted={inverted}
            isDisabled={isDisabled}
            isHoverable={isHoverable}
            iconRight={iconRight}
            onClick={onClick}
            css={[
              { lineHeight: `${size}px` },
              {
                ...styles,
              },
              maxWidth && { maxWidth },
            ]}
          >
            <title id={`icon-${uid}`}>{title}</title>
            {!!children &&
              React.Children.map(children, (child: ReactElement) =>
                React.cloneElement(child, {
                  role: 'presentation',
                })
              )}
          </SVG>
        )}
      </UIDConsumer>
    </UIDReset>
  )
}

export default Icon
